/**
 * WorkflowRail
 * Always-on desktop right rail (≥1024px only, hidden on mobile).
 * Collapsed: ~48px wide — shows pip badges per active workflow.
 * Expanded: ~400px wide — shows WorkflowRailContent (inline, no drill-in).
 *
 * The rail is a sibling to <main> so it doesn't affect the content layout.
 * It uses fixed positioning to stay in the viewport while scrolling.
 * It starts below the desktop header (top-14) so it doesn't overlap the
 * NotificationBell or the WorkflowToggle button in the header.
 */

import { useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useWorkflowPanel } from './useWorkflowPanel';
import { WorkflowRailContent } from './WorkflowRailContent';
import { workflowDisplayLabel } from '../../types/workflow';

export function WorkflowRail() {
  const { workflows, isRailExpanded, toggleRail } = useWorkflowPanel();

  // NOTE: the desktop rail intentionally does NOT collapse when a CTA navigates. Unlike the
  // mobile sheet (a full-screen overlay that must close to reveal the destination), the rail is
  // a side panel that coexists with page content — keeping it open preserves the landlord's
  // place in the workflow and lets it live-update as they complete steps on the destination page.

  // Synchronize rail width as a CSS custom property on <html> so AppShell's
  // <main> can compensate its right padding without prop-drilling.
  // collapsed = 3rem (w-12), expanded = 25rem (w-[25rem])
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--workflow-rail-width', isRailExpanded ? '25rem' : '3rem');
    return () => {
      root.style.removeProperty('--workflow-rail-width');
    };
  }, [isRailExpanded]);

  const activeWorkflows = workflows.filter((w) => w.status !== 'dismissed');

  // Desktop-only: hidden on mobile with CSS (lg:flex, not rendered via JS breakpoint to avoid flash)
  return (
    <div
      id="workflow-rail"
      data-testid="workflow-rail"
      className={`
        hidden lg:flex flex-col
        fixed top-14 right-0 bottom-0 z-30
        bg-background border-l border-border shadow-sm
        transition-all duration-300 ease-in-out
        ${isRailExpanded ? 'w-[25rem]' : 'w-12'}
      `}
      aria-label="Workflow panel"
      role="complementary"
    >
      {/* Collapsed state: pip per workflow */}
      {!isRailExpanded && activeWorkflows.length > 0 && (
        <div className="flex flex-col items-center gap-2 pt-3 px-2" aria-hidden="true">
          {activeWorkflows
            .slice(0, 5)
            .map((workflow) => {
              const completed = workflow.members.filter((m) => m.status === 'completed').length;
              const total = workflow.members.length;
              const pct = total > 0 ? (completed / total) * 100 : 0;
              return (
                <button
                  key={workflow.id}
                  type="button"
                  onClick={toggleRail}
                  className="w-7 h-7 rounded-full border-2 border-border bg-muted hover:border-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  style={{
                    background: `conic-gradient(hsl(var(--primary)) ${pct}%, hsl(var(--muted)) ${pct}%)`,
                  }}
                  aria-label="View workflow"
                  title={workflowDisplayLabel(workflow)}
                />
              );
            })}
        </div>
      )}

      {/* Expanded state: full panel */}
      {isRailExpanded && (
        <div
          data-testid="workflow-rail-panel"
          className="flex-1 overflow-y-auto overscroll-contain"
        >
          {/* Panel header row */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Workflows</h2>
            <button
              type="button"
              onClick={toggleRail}
              className="flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Collapse workflow panel"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <WorkflowRailContent />
        </div>
      )}
    </div>
  );
}
